import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, CheckCircle, XCircle, ChevronRight, Award, Clock, BookOpen, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const QuizPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiCall("GET", "/quizzes").then(setQuizzes).catch(console.error).finally(() => setLoading(false));
  }, []);

  const startQuiz = async (quizId) => {
    try {
      const data = await apiCall("GET", `/quizzes/${quizId}`);
      setActiveQuiz(data);
      setQuestions(data.questions);
      setAnswers(new Array(data.questions.length).fill(-1));
      setCurrentQ(0);
      setResult(null);
    } catch (err) { toast.error("Failed to load quiz"); }
  };

  const selectAnswer = (idx) => {
    const newAnswers = [...answers];
    newAnswers[currentQ] = idx;
    setAnswers(newAnswers);
  };

  const submitQuiz = async () => {
    if (!user || !token) { toast.error("Please sign in"); return; }
    if (answers.includes(-1)) { toast.error("Please answer all questions"); return; }
    try {
      const res = await apiCall("POST", `/quizzes/${activeQuiz.id}/submit`, { answers }, token);
      setResult(res);
    } catch (err) { toast.error("Failed to submit"); }
  };

  if (activeQuiz && !result) {
    const q = questions[currentQ];
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="quiz-active">
        <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-4">
          <div className="max-w-3xl mx-auto px-4 flex items-center justify-between">
            <div>
              <h2 className="font-bold">{activeQuiz.title}</h2>
              <p className="text-white/60 text-xs">Question {currentQ + 1} of {questions.length}</p>
            </div>
            <div className="w-32 h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-[#FF6B00] rounded-full transition-all" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
            </div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Card className="shadow-2xl" data-testid="question-card">
            <CardContent className="p-6 space-y-5">
              <h3 className="text-xl font-bold text-[#002147]">{q.question}</h3>
              <div className="space-y-3">
                {q.options.map((opt, i) => (
                  <button key={i} onClick={() => selectAnswer(i)}
                    className={`w-full text-left px-5 py-4 rounded-xl font-medium text-sm transition-all ${
                      answers[currentQ] === i ? "bg-blue-600 text-white" : "bg-gray-50 text-[#002147] hover:bg-gray-100 border border-gray-200"
                    } ${btn3D}`}
                    data-testid={`option-${i}`}>
                    <span className="font-bold mr-3">{String.fromCharCode(65 + i)}.</span> {opt}
                  </button>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                {currentQ > 0 && <button onClick={() => setCurrentQ(currentQ - 1)} className={`px-5 py-2.5 bg-gray-200 text-[#002147] font-medium rounded-xl ${btn3D}`}>Previous</button>}
                <div className="flex-1" />
                {currentQ < questions.length - 1 ? (
                  <button onClick={() => setCurrentQ(currentQ + 1)} disabled={answers[currentQ] === -1} className={`px-5 py-2.5 bg-[#002147] text-white font-medium rounded-xl disabled:opacity-30 ${btn3D}`} data-testid="next-btn">Next <ChevronRight size={14} className="inline" /></button>
                ) : (
                  <button onClick={submitQuiz} disabled={answers.includes(-1)} className={`px-6 py-2.5 bg-green-600 text-white font-bold rounded-xl disabled:opacity-30 ${btn3D}`} data-testid="submit-quiz-btn">Submit Quiz</button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (result) {
    const emoji = result.score >= 80 ? "Excellent!" : result.score >= 60 ? "Good Job!" : result.score >= 40 ? "Keep Trying!" : "Study More!";
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center p-4" data-testid="quiz-result">
        <Card className="max-w-md w-full shadow-2xl">
          <CardContent className="p-8 text-center space-y-4">
            <div className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center ${result.score >= 60 ? "bg-green-100" : "bg-red-100"}`}>
              <span className="text-4xl font-black text-[#002147]">{result.score}%</span>
            </div>
            <h2 className="text-2xl font-bold text-[#002147]">{emoji}</h2>
            <p className="text-gray-600">You got <strong>{result.correct}</strong> out of <strong>{result.total}</strong> correct</p>
            <div className="space-y-2 text-left">
              {questions.map((q, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {result.answers[i] === result.correct_answers[i] ? <CheckCircle size={16} className="text-green-500" /> : <XCircle size={16} className="text-red-500" />}
                  <span className="text-gray-700 truncate">{q.question}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setActiveQuiz(null); setResult(null); }} className={`flex-1 py-3 bg-gray-200 text-[#002147] font-semibold rounded-xl ${btn3D}`}><RotateCcw size={14} className="inline mr-1" /> All Quizzes</button>
              <button onClick={() => startQuiz(activeQuiz.id)} className={`flex-1 py-3 bg-[#002147] text-white font-semibold rounded-xl ${btn3D}`}>Retry</button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="quiz-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-8">
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3"><Award size={28} /><div><h1 className="text-2xl font-bold">Interactive Quizzes</h1><p className="text-white/70 text-sm">Test your knowledge — instant results!</p></div></div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {quizzes.map(q => (
            <Card key={q.id} className="hover:shadow-lg transition-shadow" data-testid={`quiz-card-${q.id}`}>
              <CardContent className="p-5">
                <Badge className="bg-blue-100 text-blue-700 mb-2">{q.subject}</Badge>
                <h3 className="font-bold text-[#002147]">{q.title}</h3>
                <p className="text-xs text-gray-400 mt-1"><Clock size={10} className="inline mr-1" />{q.class_level} &bull; {q.question_count} questions</p>
                <p className="text-xs text-gray-400">By {q.teacher_name}</p>
                <button onClick={() => user ? startQuiz(q.id) : toast.error("Sign in to take quizzes")} className={`w-full mt-3 py-2.5 bg-green-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2 ${btn3D}`} data-testid={`start-${q.id}`}>
                  <Play size={16} /> Start Quiz
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
